import { createRouter, createWebHashHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHashHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      meta: { public: true },
      component: () => import('@/views/LoginView.vue')
    },
    {
      path: '/change-password',
      name: 'change-password',
      component: () => import('@/views/ChangePasswordView.vue')
    },
    {
      path: '/forbidden',
      name: 'forbidden',
      component: () => import('@/views/ForbiddenView.vue')
    },
    {
      path: '/legal/privacy',
      name: 'privacy-notice',
      meta: { public: true },
      component: () => import('@/views/PrivacyNoticeView.vue')
    },
    {
      path: '/legal/usage',
      name: 'usage-notice',
      meta: { public: true },
      component: () => import('@/views/UsageNoticeView.vue')
    },
    {
      path: '/support/contact',
      name: 'support-contact',
      meta: { public: true },
      component: () => import('@/views/SupportContactView.vue')
    },
    {
      path: '/',
      redirect: '/data-service'
    },
    {
      path: '/data-service',
      name: 'data-service',
      meta: { permission: 'dashboard:read' },
      component: () => import('@/views/dataService/DataServiceDashboard.vue')
    },
    {
      path: '/data-service/catalog',
      redirect: '/data-service/sources'
    },
    {
      path: '/data-service/sources',
      name: 'data-service-sources',
      meta: { anyPermission: ['datasource:read', 'dataset:read'] },
      component: () => import('@/views/dataService/DataSourceExplorerView.vue')
    },
    {
      path: '/data-service/datasets/:id',
      name: 'data-service-dataset-detail',
      meta: { permission: 'dataset:read' },
      component: () => import('@/views/dataService/DatasetDetailView.vue')
    },
    {
      path: '/data-service/products',
      name: 'data-service-products',
      meta: { permission: 'product:read' },
      component: () => import('@/views/dataService/DataProductView.vue')
    },
    {
      path: '/data-service/products/create',
      name: 'data-service-products-create',
      meta: { permission: 'product:write' },
      component: () => import('@/views/dataService/DataProductEditorView.vue')
    },
    {
      path: '/data-service/products/:id/edit',
      name: 'data-service-products-edit',
      meta: { permission: 'product:write' },
      component: () => import('@/views/dataService/DataProductEditorView.vue')
    },
    {
      path: '/data-service/operations',
      name: 'data-service-operations',
      meta: { permission: 'operations:read' },
      component: () => import('@/views/dataService/DataOperationsView.vue')
    },
    {
      path: '/data-service/operations/create',
      name: 'data-service-operations-create',
      meta: { permission: 'operations:write' },
      component: () => import('@/views/dataService/SyncPolicyEditorView.vue')
    },
    {
      path: '/data-service/operations/:id/edit',
      name: 'data-service-operations-edit',
      meta: { permission: 'operations:write' },
      component: () => import('@/views/dataService/SyncPolicyEditorView.vue')
    },
    {
      path: '/iam',
      name: 'iam',
      meta: { anyPermission: ['iam:user:manage', 'iam:role:manage', 'iam:token:manage'] },
      component: () => import('@/views/IamManagementView.vue')
    },
    {
      path: '/strategy-service',
      name: 'strategy-service',
      component: () => import('@/views/StrategyServiceView.vue')
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/data-service'
    }
  ]
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if (!auth.initialized && !to.meta.public) {
    await auth.fetchCurrentUser()
  }
  if (to.meta.public) {
    if (to.name === 'login' && auth.isAuthenticated) {
      return auth.forcePasswordChange ? '/change-password' : '/data-service'
    }
    return true
  }
  if (!auth.isAuthenticated) {
    return { path: '/login', query: { redirect: to.fullPath } }
  }
  if (auth.forcePasswordChange && to.path !== '/change-password') {
    return '/change-password'
  }
  const requiredPermission = to.meta.permission as string | undefined
  if (requiredPermission && !auth.hasPermission(requiredPermission)) {
    return '/forbidden'
  }
  const anyPermission = to.meta.anyPermission as string[] | undefined
  if (anyPermission && !auth.hasAnyPermission(anyPermission)) {
    return '/forbidden'
  }
  return true
})

export default router
